import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { useSelector } from 'react-redux';
import type { FC } from 'react';
import { CASH_DUE_TODAY, CASH_RECEIVED } from '../constants';
import { InlineGroupContainer } from '../dealSummary/common/ComponentLabelStyle';
import CashAmountPaid, { type CashFieldVariant } from './CashAmountPaid.interstate';
import ReadOnlyCashField from './ReadOnlyCashField.interstate';

type CashFieldsProps = {
    onInputErrors?: (message: string) => void;
    variant: CashFieldVariant;
};

/**
 * Renders the read-only "Cash Due Today" and the editable "Cash Received" fields side-by-side for lease deals
 * behind enableEquityAllocationOrder (US2067616 / US2067622). Both surfaces read the same
 * `cashDueFromCustomer` and neither field branches on the equity allocation order.
 *
 * The `slideout` variant writes the Due at Signing down payment override, triggers a preview recalculation,
 * and prefers the in-flight preview value; the `summary` variant commits to the deal and reads the committed
 * offer value. The summary copy stays mounted behind the open slideout, so both labels are on screen twice
 * with different amounts for as long as an uncommitted override is in play — not just while a recalculation
 * is in flight — until the user applies or cancels.
 */
const CashFields: FC<CashFieldsProps> = ({ onInputErrors, variant }) => {
    const isSummaryVariant = variant === 'summary';
    const cashDueTodayValue = useSelector(
        isSummaryVariant ? offerReduxSelectors.getCashDueFromCustomer : dueAtSigningSelectors.getDasCashDueFromCustomer
    );
    const isDasLocked = useSelector(dueAtSigningSelectors.getIsDasLocked);

    return (
        <InlineGroupContainer>
            <ReadOnlyCashField
                label={CASH_DUE_TODAY}
                value={cashDueTodayValue}
                testId={isSummaryVariant ? 'cash-due-today-summary-readonly-value' : 'cash-due-today-readonly-value'}
                variant={variant}
                // The summary field reflects the committed deal, so a slideout recalculation must not disable it.
                disabled={isSummaryVariant ? undefined : isDasLocked}
            />
            <CashAmountPaid label={CASH_RECEIVED} onInputErrors={onInputErrors} variant={variant} />
        </InlineGroupContainer>
    );
};

export default CashFields;
